"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import { EventName } from "@mw/shared/analyticsEventNames";

import { captureEvent } from "@/app/_analytics/analytics";
import type { FlowTemplateSchema } from "@/app/_api/flowTemplatesServer";
import { useAutoDismissError } from "@/app/_ui/hooks/useAutoDismissError";
import { toCtaUrl } from "@/app/_views/CreativeTemplate/ctaUrls";
import {
  createFlowFileValidator,
  runFlowSubmit,
  type FlowSubmitForm,
} from "@/app/_views/CreativeTemplate/HeroOmnibox/hooks/runFlowSubmit";
import type { ContentWorkflowStep, WorkflowResultType } from "@/app/_views/CreativeTemplate/HeroOmnibox/types";

// Flow templates run anonymously and this generic block has no CMS source for
// the upload limit, so cap file size with a safe default. The xi API enforces
// the real limit too; this is just fast pre-upload feedback.
const MAX_FILE_SIZE_MB = 20;

// The block reuses the omnibox funnel event *names* so the demo's interactions
// land on the same PostHog insights, but tags every event with `source` so they
// can be told apart from the real creative-template page (and stay out of the
// page's template_category/variant_slug-keyed breakdowns). Each surface that
// embeds this run passes its own `source` — the standalone block defaults to
// "flow_template_block", the gallery passes "flow_template_gallery" — so the two
// can also be told apart from each other.
const DEFAULT_ANALYTICS_SOURCE = "flow_template_block";

/**
 * Headless run controller for the standalone Flow Template block. Delegates the
 * upload→run→error orchestration to the shared {@link runFlowSubmit} engine —
 * the same one the CreativeTemplate omnibox uses — and owns only this surface's
 * state, abort controller and analytics adapter.
 *
 * Intentionally drops CreativeTemplate coupling (templateSlug, generationModel,
 * legacy redirect templates, hero-media object-URL bookkeeping — the form card
 * owns its own previews); analytics fire the omnibox events tagged with the
 * caller's `analyticsSource` instead of the page's template/variant props.
 */
export function useFlowTemplateRun({
  flowTemplate,
  resultType,
  analyticsSource = DEFAULT_ANALYTICS_SOURCE,
}: {
  flowTemplate: FlowTemplateSchema;
  resultType: WorkflowResultType;
  /**
   * PostHog `source` tag for this surface. Defaults to "flow_template_block"
   * (the standalone block); the gallery passes "flow_template_gallery" so the
   * two surfaces are distinguishable in analytics.
   */
  analyticsSource?: string;
}) {
  const [step, setStep] = useState<ContentWorkflowStep>("idle");
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [redirectCtaUrl, setRedirectCtaUrl] = useState<string | null>(null);
  const { error, setError } = useAutoDismissError();

  const t = useTranslations("ClientKeys.creative_templates");
  const abortControllerRef = useRef<AbortController | null>(null);

  // Abort any in-flight run on unmount.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, []);

  const validateFile = useMemo(() => createFlowFileValidator({ maxFileSizeMb: MAX_FILE_SIZE_MB, t }), [t]);

  const handleFlowSubmit = useCallback(
    async (form: FlowSubmitForm) => {
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      setError(null);

      await runFlowSubmit({
        form,
        flowTemplate,
        resultType,
        signal: abortController.signal,
        t,
        setStep,
        setError,
        setResultUrl,
        setRedirectCtaUrl,
        toCtaUrl,
        analytics: {
          onStarted: ({ input_type }) =>
            captureEvent(EventName.mw_creative_template_omnibox_generation_started, {
              source: analyticsSource,
              input_type,
            }),
          onSucceeded: ({ input_type }) =>
            captureEvent(EventName.mw_creative_template_omnibox_generation_succeeded, {
              source: analyticsSource,
              input_type,
            }),
          onQuotaExceeded: ({ error_type }) =>
            captureEvent(EventName.mw_creative_template_omnibox_quota_exceeded, {
              source: analyticsSource,
              error_type,
            }),
          onFailed: ({ error_type, error_message }) =>
            captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
              source: analyticsSource,
              ...(error_type ? { error_type } : {}),
              ...(error_message ? { error_message } : {}),
            }),
        },
      });
    },
    [flowTemplate, resultType, analyticsSource, setError, t],
  );

  return {
    step,
    error,
    resultUrl,
    redirectCtaUrl,
    validateFile,
    handleFlowSubmit,
  };
}
